import { lazy, Suspense, useEffect, useMemo } from 'react';
import { Refine } from '@refinedev/core';
import { useNotificationProvider } from '@refinedev/antd';
import routerProvider from '@refinedev/react-router';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import type { MsalAuthenticationResult } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import {
  BrowserRouter,
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router';
import { App as AntApp, Button, ConfigProvider, Result, Spin } from 'antd';
import {
  DashboardOutlined,
  IdcardOutlined,
  BankOutlined,
  CalendarOutlined,
  ApiOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
  RobotOutlined,
  DeploymentUnitOutlined,
  FileTextOutlined,
  ReadOutlined,
  ScanOutlined,
  LineChartOutlined,
  ContactsOutlined,
} from '@ant-design/icons';
import '@refinedev/antd/dist/reset.css';

import {
  ThemeModeProvider,
  useThemeMode,
  buildVendoiqTheme,
} from '@viq-admin/theme';
import { AdminLayout, Wordmark, CapabilityProvider } from '@viq-admin/ui';
import type { AdminNavGroup, AdminNavItem } from '@viq-admin/ui';

import { msalInstance, API_SCOPE } from './auth/msalConfig';
import { SIGNED_OUT_KEY } from './auth/signedOutMarker';
import {
  AbilityProvider,
  buildAbilityFromClaims,
  useAbility,
} from './ability/AbilityContext';
import { buildAccessControlProvider } from './ability/accessControl';
import { dataProvider } from './api/dataProvider';
import { queryClient } from './api/queryClient';
import {
  CompanyScopeBanner,
  CompanyScopePicker,
  CompanyScopeProvider,
} from './components/CompanyScopePicker';
import { RequireCan } from './components/RequireCan';
import { HeaderSearch } from './search/HeaderSearch';
import { buildConsoleCan } from './capability/consoleCapabilities';

// Resource pages (Companies → Leads), all committed in the earlier P4 tasks.
import { CompanyList } from './resources/companies/list';
import { CompanyArchivedList } from './resources/companies/archivedList';
import { CompanyCreate } from './resources/companies/create';
import { CompanyDetail } from './resources/companies/CompanyDetail';
import { ConferenceList } from './resources/conferences/list';
import { ConferenceArchivedList } from './resources/conferences/archivedList';
import { ConferenceCreate } from './resources/conferences/create';
import { ConferenceShow } from './resources/conferences/show';
import { UserList } from './resources/users/list';
import { UserCreate } from './resources/users/create';
import { UserEdit } from './resources/users/edit';
import { UserDetailView } from './views/UserDetail/UserDetailView';
import { ToolkitsList } from './resources/toolkits/list';
import { ToolkitsCreate } from './resources/toolkits/create';
import { ToolkitDetail } from './resources/toolkits/ToolkitDetail';
import { PromptTemplatesList } from './resources/prompt-templates/list';
import { PromptTemplatesCreate } from './resources/prompt-templates/create';
import { PromptTemplateDetail } from './resources/prompt-templates/PromptTemplateDetail';
import { CorePromptsList } from './resources/core-prompts/list';
import { PromptRolloutList } from './resources/prompt-rollout/list';
import { PromptGuide } from './resources/prompt-guide/PromptGuide';
import { ScanPaths } from './resources/scan-paths/ScanPaths';
import { IntegrationList } from './resources/integrations/list';
import { IntegrationCreate } from './resources/integrations/create';
import { IntegrationDetail } from './resources/integrations/IntegrationDetail';
import { QuestionList } from './resources/questions/list';
import { QuestionCreate } from './resources/questions/create';
import { QuestionDetail } from './resources/questions/QuestionDetail';
import { LeadsList } from './views/Leads/LeadsList';
import { LeadDetailView } from './views/Leads/LeadDetailView';
import { LiveFeedRoute } from './views/LiveFeed/LiveFeedRoute';
import { BatchRoute } from './views/Batch/BatchRoute';

// Lazy so Recharts lands in its own chunk and only loads on this page.
const TelemetryView = lazy(() => import('./views/Telemetry/TelemetryView'));

// Central Refine access-control gate (hides, doesn't disable, Refine's own
// CreateButton/EditButton/… for actions the signed-in role can't use — see
// ability/accessControl.ts). The ability getter reads the msal singleton
// directly (same source AppShell already reads for roleLabel below) rather
// than the AbilityContext, because this provider is constructed once at
// module scope, outside the AbilityProvider/MsalProvider subtree it gates —
// it re-reads the live claims on every check, so it always reflects the
// current signed-in role.
const consoleAccessControlProvider = buildAccessControlProvider(() =>
  buildAbilityFromClaims(
    msalInstance.getAllAccounts()[0]?.idTokenClaims as
      | { roles?: string[]; oid?: string; email?: string }
      | undefined,
  ),
);

// ---------------------------------------------------------------------------
// Auth gate — no custom sign-in page
// ---------------------------------------------------------------------------
//
// The console deliberately has NO branded "Sign in" interstitial. When there's
// no authenticated session, MsalAuthenticationTemplate (in App() below) fires
// loginRedirect immediately, so a signed-out visitor lands straight on the
// Microsoft Entra login — nothing here hints at what this site is until an
// authenticated session exists. These two screens are the only things ever
// shown pre-auth, and both are intentionally generic and unbranded.
//
// The exception is straight after an explicit sign-out: Entra's
// postLogoutRedirectUri is window.location.origin (msalConfig.ts) — this same
// app — so re-entering MsalAuthenticationTemplate there with zero accounts
// would auto-fire loginRedirect and bounce the user straight back to the
// Entra account picker (a sign-out "loop"). <SignedOut/> below is a third
// screen, rendered OUTSIDE the MsalAuthenticationTemplate gate (see the
// `signedOut` check in App()) so landing back here after logout stops
// instead of re-entering the gate. Unlike AuthLoading/AuthError it's only
// ever reached by someone who was signed in a moment ago (never a first-time
// visitor), so — unlike the rest of this section — it's safe to show the
// console's own Wordmark.

/**
 * Shown for the moment before the browser navigates to Entra (and briefly
 * while MSAL processes the redirect on the way back). Neutral by design.
 */
function AuthLoading() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Spin size="large" />
    </div>
  );
}

/**
 * Shown when a sign-in attempt fails or is cancelled. Without an explicit
 * error branch, MsalAuthenticationTemplate would re-enter the redirect on the
 * next render and loop; a manual Retry breaks that. Copy stays generic so a
 * failed load still reveals nothing about the app.
 */
function AuthError({ login }: MsalAuthenticationResult) {
  return (
    <Result
      status="warning"
      title="Sign-in required"
      subTitle="We couldn't complete sign-in. Please try again."
      extra={
        <Button
          type="primary"
          onClick={() =>
            login(InteractionType.Redirect, { scopes: [API_SCOPE] })
          }
        >
          Retry
        </Button>
      }
    />
  );
}

/**
 * Rendered instead of <MsalAuthenticationTemplate> right after an explicit
 * sign-out (see the `signedOut` check in App(), below) — the passive
 * counterpart to AuthError/AuthLoading above. Its "Sign in" button clears the
 * marker itself before starting a fresh interactive login, the same request
 * shape a first-time visitor's auto-redirect would use.
 */
function SignedOut() {
  return (
    <Result
      icon={<Wordmark height={48} />}
      title="Signed out"
      subTitle="You've been signed out."
      extra={
        <Button
          type="primary"
          onClick={() => {
            sessionStorage.removeItem(SIGNED_OUT_KEY);
            msalInstance.loginRedirect({ scopes: [API_SCOPE] });
          }}
        >
          Sign in
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// App shell with sidebar nav
// ---------------------------------------------------------------------------

// The IQ Panel's sections, ordered by how often a super admin reaches for
// them: the everyday records first, then the fleet, then setup, with the AI
// prompt store folded away (shut unless you're on one of its pages) and the
// pipeline reference last.
const DAILY: AdminNavGroup = { key: 'daily', label: 'Daily' };
const FLEET: AdminNavGroup = { key: 'fleet', label: 'Fleet' };
const SETUP: AdminNavGroup = { key: 'setup', label: 'Setup' };
const AI_PROMPTS: AdminNavGroup = {
  key: 'ai-prompts',
  label: 'AI prompts',
  collapsible: true,
  icon: <RobotOutlined />,
};
const REFERENCE: AdminNavGroup = { key: 'reference', label: 'Reference' };

// Every console page MUST be listed here to be reachable: this console renders
// a hand-maintained nav, not Refine's auto-generated menu, so registering a
// Refine resource alone leaves the screen routable-but-unlinked.
const SA_NAV: AdminNavItem[] = [
  { key: 'live-feed', label: 'Live feed', href: '/live-feed', icon: <DashboardOutlined />, group: DAILY },
  { key: 'batch', label: 'Batch', href: '/batch', icon: <IdcardOutlined />, group: DAILY },
  { key: 'leads', label: 'Leads', href: '/leads', icon: <ContactsOutlined />, group: DAILY },
  { key: 'companies', label: 'Companies', href: '/companies', icon: <BankOutlined />, group: DAILY },
  { key: 'users', label: 'Users', href: '/users', icon: <TeamOutlined />, group: DAILY },
  { key: 'conferences', label: 'Conferences', href: '/conferences', icon: <CalendarOutlined />, group: DAILY },
  { key: 'telemetry', label: 'Telemetry', href: '/telemetry', icon: <LineChartOutlined />, group: FLEET },
  { key: 'toolkits', label: 'Toolkits', href: '/toolkits', icon: <ApiOutlined />, group: SETUP },
  { key: 'integrations', label: 'Integrations (CRM)', href: '/integrations', icon: <ApiOutlined />, group: SETUP },
  { key: 'questions', label: 'Questions Inventory', href: '/questions', icon: <QuestionCircleOutlined />, group: SETUP },
  // AI prompt store (epic viq-backend#41), ordered authoring → rollout →
  // templates → the guide to all three.
  { key: 'core-prompts', label: 'AI Core Prompts', href: '/core-prompts', icon: <RobotOutlined />, group: AI_PROMPTS },
  { key: 'prompt-rollout', label: 'AI Prompt Rollout', href: '/prompt-rollout', icon: <DeploymentUnitOutlined />, group: AI_PROMPTS },
  { key: 'prompt-templates', label: 'AI Prompt Templates', href: '/prompt-templates', icon: <FileTextOutlined />, group: AI_PROMPTS },
  { key: 'prompt-guide', label: 'AI Prompt Guide', href: '/prompt-guide', icon: <ReadOutlined />, group: AI_PROMPTS },
  { key: 'scan-paths', label: 'Scan Paths', href: '/scan-paths', icon: <ScanOutlined />, group: REFERENCE },
];

/**
 * Bridges the console's CASL ability into the shared capability seam so
 * packages/ui components (DataTable, RowActionsMenu) gate identically to the
 * app's own <Can>. Must render inside <AbilityProvider>.
 */
function ConsoleCapabilities({ children }: { children: React.ReactNode }) {
  const ability = useAbility();
  const value = useMemo(
    () => ({
      can: buildConsoleCan(ability),
      renderDenied: () => <div>Access denied</div>,
    }),
    [ability],
  );
  return <CapabilityProvider value={value}>{children}</CapabilityProvider>;
}

// Console shell = the shared AdminLayout with super-admin content. The wordmark
// + "- Console" suffix marks the plane (the customer portal shows the wordmark
// alone), so staff always know they're in the cross-tenant console.
function AppShell({ children }: { children: React.ReactNode }) {
  const account = msalInstance.getAllAccounts()[0];
  const roles = ((account?.idTokenClaims ?? {}) as { roles?: string[] }).roles ?? [];
  const roleLabel = roles.includes('SuperAdmin')
    ? 'Super Admin'
    : roles.includes('SuperAdminReadOnly')
      ? 'Super Admin (Read Only)'
      : undefined;
  // Subscribe to location so the shell re-renders on client-side nav and the
  // active-item highlight tracks the clicked tab (AdminLayout sits above the
  // <Outlet>, so it can't derive this reactively on its own).
  const location = useLocation();

  // Longest-prefix match so /conferences/abc still highlights "Conferences".
  const selectedKey = SA_NAV
    .filter((n) => location.pathname === n.href || location.pathname.startsWith(`${n.href}/`))
    .sort((a, b) => b.href.length - a.href.length)[0]?.key;

  return (
    <AdminLayout
      brand={
        <>
          <Wordmark />
          <span style={{ fontWeight: 600 }}>- Console</span>
        </>
      }
      navItems={SA_NAV}
      renderNavLink={(item, children) => <Link to={item.href}>{children}</Link>}
      selectedKey={selectedKey}
      headerSearch={<HeaderSearch />}
      headerExtras={<CompanyScopePicker />}
      user={{ name: account?.name, email: account?.username, roleLabel }}
      onSignOut={() => {
        // Read by App.tsx's `signedOut` gate — without it, landing back at
        // postLogoutRedirectUri (this same origin) re-enters
        // MsalAuthenticationTemplate with zero accounts and auto-redirects
        // straight back into Entra (the sign-out loop).
        sessionStorage.setItem(SIGNED_OUT_KEY, '1');
        msalInstance.logoutRedirect({ account: msalInstance.getActiveAccount() ?? undefined });
      }}
    >
      {/* Only shows on the list pages "Operate as" narrows, while it's set. */}
      <CompanyScopeBanner />
      {children}
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// Main app
// ---------------------------------------------------------------------------

// Applies the shared VendoIQ theme (light/dark) from @viq-admin/theme. Must be
// rendered inside <ThemeModeProvider> so useThemeMode() resolves.
//
// Wrapping in antd's <App> (aliased AntApp to avoid clashing with this
// module's own `App` component) mounts the App context so message/modal
// dialogs (e.g. useConfirmAction) render inside the configured theme instead
// of bypassing it — see packages/ui/src/useConfirmAction.tsx.
function ThemedApp({ children }: { children: React.ReactNode }) {
  const { mode } = useThemeMode();
  return (
    <ConfigProvider theme={buildVendoiqTheme(mode)}>
      <AntApp>{children}</AntApp>
    </ConfigProvider>
  );
}

export function App() {
  // Straight after an explicit sign-out, Entra's postLogoutRedirectUri lands
  // back on this exact origin (msalConfig.ts) with zero MSAL accounts. Left
  // to MsalAuthenticationTemplate alone, that reads as "never signed in" and
  // auto-fires loginRedirect — the sign-out loop. AppShell's onSignOut sets
  // SIGNED_OUT_KEY before calling logoutRedirect(); render the passive
  // <SignedOut/> screen instead of the gate while it's set and there's truly
  // no account.
  const accounts = msalInstance.getAllAccounts();
  const signedOut = accounts.length === 0 && sessionStorage.getItem(SIGNED_OUT_KEY) === '1';

  // Robustness: once an account is actually observed, scrub the marker. A
  // stale '1' left over from a past sign-out would otherwise keep matching on
  // every later render — including a FUTURE session expiry that has nothing
  // to do with an explicit sign-out — and incorrectly show "signed out"
  // instead of letting MsalAuthenticationTemplate auto-redirect to login the
  // way an expiry should.
  useEffect(() => {
    if (accounts.length > 0) {
      sessionStorage.removeItem(SIGNED_OUT_KEY);
    }
  }, [accounts.length]);

  return (
    <MsalProvider instance={msalInstance}>
      <ThemeModeProvider>
        <ThemedApp>
        {signedOut ? (
        <SignedOut />
        ) : (
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={{ scopes: [API_SCOPE] }}
          loadingComponent={AuthLoading}
          errorComponent={AuthError}
        >
          {/*
           * AbilityProvider reads `roles` from the signed-in account's
           * id-token claims and builds the CASL ability.
           *
           * CompanyScopeProvider fetches the cross-tenant company list and
           * exposes the active companyId selection to the subtree.
           */}
          <AbilityProvider>
            <ConsoleCapabilities>
            <CompanyScopeProvider>
              <BrowserRouter>
                <Refine
                  dataProvider={dataProvider}
                  routerProvider={routerProvider}
                  notificationProvider={useNotificationProvider}
                  accessControlProvider={consoleAccessControlProvider}
                  resources={[
                    {
                      name: 'companies',
                      list: '/companies',
                      create: '/companies/new',
                      edit: '/companies/:id/edit',
                      show: '/companies/:id',
                    },
                    {
                      name: 'conferences',
                      list: '/conferences',
                      create: '/conferences/new',
                      edit: '/conferences/:id/edit',
                      show: '/conferences/:id',
                    },
                    {
                      name: 'users',
                      list: '/users',
                      create: '/users/new',
                      edit: '/users/:id/edit',
                    },
                    {
                      name: 'toolkits',
                      list: '/toolkits',
                      create: '/toolkits/new',
                      edit: '/toolkits/:id/edit',
                      show: '/toolkits/:id',
                    },
                    {
                      name: 'prompt-templates',
                      list: '/prompt-templates',
                      create: '/prompt-templates/new',
                      edit: '/prompt-templates/:id/edit',
                      show: '/prompt-templates/:id',
                      meta: { label: 'AI Prompt Templates' },
                    },
                    {
                      name: 'core-prompts',
                      list: '/core-prompts',
                      meta: { label: 'AI Core Prompts' },
                    },
                    {
                      name: 'prompt-rollout',
                      list: '/prompt-rollout',
                      meta: { label: 'AI Prompt Rollout' },
                    },
                    {
                      name: 'integrations',
                      list: '/integrations',
                      create: '/integrations/new',
                      edit: '/integrations/:id/edit',
                      show: '/integrations/:id',
                      meta: { label: 'Integrations (CRM)' },
                    },
                    {
                      name: 'questions',
                      list: '/questions',
                      create: '/questions/new',
                      edit: '/questions/:id/edit',
                      show: '/questions/:id',
                      meta: { label: 'Questions Inventory' },
                    },
                  ]}
                  options={{
                    syncWithLocation: false,
                    warnWhenUnsavedChanges: true,
                    disableTelemetry: true,
                    // Share the console's QueryClient so CompanyScopeProvider's
                    // invalidateQueries() (on scope change) reaches Refine's
                    // queries + the shared screens' ad-hoc useQuery calls.
                    reactQuery: { clientConfig: queryClient },
                  }}
                >
                  <AppShell>
                    <Routes>
                      <Route element={<Outlet />}>
                        <Route path="/" element={<Navigate to="/companies" replace />} />

                        {/* Companies */}
                        <Route
                          path="/companies"
                          element={
                            <RequireCan I="read" a="Company">
                              <CompanyList />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/companies/archived"
                          element={
                            <RequireCan I="read" a="Company">
                              <CompanyArchivedList />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/companies/new"
                          element={
                            <RequireCan I="manage" a="Company">
                              <CompanyCreate />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/companies/:id/edit"
                          element={<Navigate to="../" relative="path" replace />}
                        />
                        <Route
                          path="/companies/:id"
                          element={
                            <RequireCan I="read" a="Company">
                              <CompanyDetail />
                            </RequireCan>
                          }
                        />

                        {/* Conferences */}
                        <Route
                          path="/conferences"
                          element={
                            <RequireCan I="read" a="Conference">
                              <ConferenceList />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/conferences/archived"
                          element={
                            <RequireCan I="read" a="Conference">
                              <ConferenceArchivedList />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/conferences/new"
                          element={
                            <RequireCan I="manage" a="Conference">
                              <ConferenceCreate />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/conferences/:id/edit"
                          element={<Navigate to="../" relative="path" replace />}
                        />
                        <Route
                          path="/conferences/:id"
                          element={
                            <RequireCan I="read" a="Conference">
                              <ConferenceShow />
                            </RequireCan>
                          }
                        />

                        {/* Users — pages resolve either :id or :oid */}
                        <Route
                          path="/users"
                          element={
                            <RequireCan I="read" a="User">
                              <UserList />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/users/new"
                          element={
                            <RequireCan I="manage" a="User">
                              <UserCreate />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/users/:id/edit"
                          element={
                            <RequireCan I="manage" a="User">
                              <UserEdit />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/users/:id"
                          element={
                            <RequireCan I="read" a="User">
                              <UserDetailView />
                            </RequireCan>
                          }
                        />

                        {/* Mobile fleet telemetry — read-only; the backend gates on read User too */}
                        <Route
                          path="/telemetry"
                          element={
                            <RequireCan I="read" a="User">
                              <Suspense fallback={<Spin style={{ display: 'block', margin: '48px auto' }} />}>
                                <TelemetryView />
                              </Suspense>
                            </RequireCan>
                          }
                        />

                        {/* Toolkits */}
                        <Route
                          path="/toolkits"
                          element={
                            <RequireCan I="read" a="Toolkit">
                              <ToolkitsList />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/toolkits/new"
                          element={
                            <RequireCan I="manage" a="Toolkit">
                              <ToolkitsCreate />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/toolkits/:id/edit"
                          element={<Navigate to="../" relative="path" replace />}
                        />
                        <Route
                          path="/toolkits/:id"
                          element={
                            <RequireCan I="read" a="Toolkit">
                              <ToolkitDetail />
                            </RequireCan>
                          }
                        />

                        {/* AI Prompt Templates */}
                        <Route path="/prompt-templates" element={
                          <RequireCan I="read" a="PromptTemplate"><PromptTemplatesList /></RequireCan>
                        } />
                        <Route path="/prompt-templates/new" element={
                          <RequireCan I="manage" a="PromptTemplate"><PromptTemplatesCreate /></RequireCan>
                        } />
                        <Route path="/prompt-templates/:id/edit" element={
                          <Navigate to="../" relative="path" replace />
                        } />
                        <Route path="/prompt-templates/:id" element={
                          <RequireCan I="read" a="PromptTemplate"><PromptTemplateDetail /></RequireCan>
                        } />

                        {/* AI Core Prompts */}
                        <Route path="/core-prompts" element={
                          <RequireCan I="read" a="CorePrompt"><CorePromptsList /></RequireCan>
                        } />

                        {/* AI Prompt Rollout */}
                        <Route path="/prompt-rollout" element={
                          <RequireCan I="read" a="CorePrompt"><PromptRolloutList /></RequireCan>
                        } />

                        {/* AI Prompt Guide — documentation, same read gate as the
                            screens it documents. */}
                        <Route path="/prompt-guide" element={
                          <RequireCan I="read" a="CorePrompt"><PromptGuide /></RequireCan>
                        } />

                        {/* Scan Paths — documentation of the mobile capture
                            pipeline, gated on reading the thing it describes the
                            creation of. Both super-admin roles can read a Lead,
                            so full and read-only admins both see this page.
                            Deliberately NOT a new CASL subject: superadmin.ts is
                            a hand-synced vendored copy of the backend's factory,
                            and a doc page is not worth diverging it. */}
                        <Route path="/scan-paths" element={
                          <RequireCan I="read" a="Lead"><ScanPaths /></RequireCan>
                        } />

                        {/* Integrations (CRM) */}
                        <Route
                          path="/integrations"
                          element={
                            <RequireCan I="read" a="Integration">
                              <IntegrationList />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/integrations/new"
                          element={
                            <RequireCan I="manage" a="Integration">
                              <IntegrationCreate />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/integrations/:id/edit"
                          element={<Navigate to="../" relative="path" replace />}
                        />
                        <Route
                          path="/integrations/:id"
                          element={
                            <RequireCan I="read" a="Integration">
                              <IntegrationDetail />
                            </RequireCan>
                          }
                        />

                        {/* Questions Inventory — per-tenant (driven by the global scope) */}
                        <Route
                          path="/questions"
                          element={
                            <RequireCan I="read" a="DiscoveryQuestion">
                              <QuestionList />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/questions/new"
                          element={
                            <RequireCan I="manage" a="DiscoveryQuestion">
                              <QuestionCreate />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/questions/:id/edit"
                          element={<Navigate to="../" relative="path" replace />}
                        />
                        <Route
                          path="/questions/:id"
                          element={
                            <RequireCan I="read" a="DiscoveryQuestion">
                              <QuestionDetail />
                            </RequireCan>
                          }
                        />

                        {/* Live feed — cross-tenant polling feed */}
                        <Route
                          path="/live-feed"
                          element={
                            <RequireCan I="read" a="Lead">
                              <LiveFeedRoute />
                            </RequireCan>
                          }
                        />

                        {/* Batch — read-only cross-tenant event workspace */}
                        <Route
                          path="/batch"
                          element={
                            <RequireCan I="read" a="Lead">
                              <BatchRoute />
                            </RequireCan>
                          }
                        />

                        {/* Leads — read-only cross-tenant */}
                        <Route
                          path="/leads"
                          element={
                            <RequireCan I="read" a="Lead">
                              <LeadsList />
                            </RequireCan>
                          }
                        />
                        <Route
                          path="/leads/:id"
                          element={
                            <RequireCan I="read" a="Lead">
                              <LeadDetailView />
                            </RequireCan>
                          }
                        />

                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Route>
                    </Routes>
                  </AppShell>
                </Refine>
              </BrowserRouter>
            </CompanyScopeProvider>
            </ConsoleCapabilities>
          </AbilityProvider>
        </MsalAuthenticationTemplate>
        )}
        </ThemedApp>
      </ThemeModeProvider>
    </MsalProvider>
  );
}
